import { useEffect, useMemo, useRef, useState } from 'react';
import {
  usePaginatedDesignations,
  useCreateDesignation,
  useUpdateDesignation,
  useDeleteDesignation,
  useBulkDeleteDesignations,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import {
  Award,
  Plus,
  Edit2,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  Search,
  Hash,
  Layers,
  AlignLeft,
  CheckCircle,
  XCircle,
} from 'lucide-react';

/** Builds an editable designation code from the name, e.g. "Senior Doctor" -> "SENIOR_DOCTOR". */
const generateDesignationCode = (name: string): string =>
  name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);

export function Designations() {
  const limit = 20;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [viewing, setViewing] = useState<any>(null);
  const [form, setForm] = useState({
    designationCode: '',
    name: '',
    description: '',
    level: 1,
    isActive: true,
  });
  // The code is auto-generated from the name until the user edits it manually.
  const [codeEdited, setCodeEdited] = useState(false);

  const { data: designationsData, isLoading, error } = usePaginatedDesignations(page, limit, debouncedSearch || undefined);
  const createDesig = useCreateDesignation();
  const updateDesig = useUpdateDesignation();
  const deleteDesig = useDeleteDesignation();
  const bulkDeleteDesig = useBulkDeleteDesignations();

  const designations = useMemo(() => designationsData?.data || [], [designationsData]);
  const meta = designationsData?.meta;

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(designations.map((d: any) => d.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [designations]);

  const allSelected = designations.length > 0 && designations.every((d: any) => selectedIds.has(d.id));
  const someSelected = designations.some((d: any) => selectedIds.has(d.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = designations.length > 0 && designations.every((d: any) => prev.has(d.id));
      return everySelected ? new Set() : new Set(designations.map((d: any) => d.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteDesig.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} designation${ids.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to delete some designations');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  const resetForm = () => {
    setForm({ designationCode: '', name: '', description: '', level: 1, isActive: true });
    setCodeEdited(false);
    setShowForm(false);
    setEditing(null);
  };

  const startEdit = (d: any) => {
    setEditing(d);
    setForm({
      designationCode: d.designationCode,
      name: d.name,
      description: d.description || '',
      level: d.level || 1,
      isActive: d.isActive,
    });
    setCodeEdited(true);
    setShowForm(true);
  };

  const handleNameChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      designationCode: codeEdited ? prev.designationCode : generateDesignationCode(value),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { ...form, level: Number(form.level) };
    if (editing) {
      updateDesig.mutate({ id: editing.id, data: payload }, { onSuccess: resetForm });
    } else {
      createDesig.mutate(payload, { onSuccess: resetForm });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDesig.mutateAsync(deleteTarget.id);
      toast.success('Designation deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete designation');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Award className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-text">Designations</h1>
            <p className="text-sm text-text-muted">Manage employee and doctor designations.</p>
          </div>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Add Designation
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load designations</span>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search designations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteDesig.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteDesig.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/25 text-[0.6875rem] font-semibold leading-none">
                {selectedIds.size}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteDesig.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={designations.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Level</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {designations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                      {search ? (
                        <span>
                          No designations match your search.{' '}
                          <button onClick={() => { setSearch(''); setPage(1); }} className="text-primary hover:underline">
                            Clear search
                          </button>
                        </span>
                      ) : (
                        'No designations configured. Add your first designation to get started.'
                      )}
                    </td>
                  </tr>
                ) : (
                  designations.map((desig: any) => (
                    <tr key={desig.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(desig.id)}
                          onChange={() => toggleOne(desig.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Award size={14} className="text-primary" />
                          </div>
                          <button
                            onClick={() => setViewing(desig)}
                            className="text-sm font-medium text-text hover:text-primary transition-colors text-left cursor-pointer"
                          >
                            {desig.name}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted font-mono">{desig.designationCode}</td>
                      <td className="px-4 py-3 text-sm text-text-muted">{desig.level ?? '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${desig.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                          {desig.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => startEdit(desig)} className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-primary transition-colors" title="Edit">
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget({ id: desig.id, name: desig.name })}
                            className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-danger transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="designations"
          />
        </div>
      )}

      {/* Details Modal */}
      {viewing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setViewing(null)}
        >
          <div
            className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <Award size={16} className="text-primary" />
                </div>
                <h2 className="text-lg font-semibold text-text">{viewing.name}</h2>
              </div>
              <button onClick={() => setViewing(null)} className="p-1 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-start gap-3 bg-background rounded-lg p-3 border border-border">
                  <div className="p-2 bg-primary/10 rounded-lg"><Hash size={15} className="text-primary" /></div>
                  <div>
                    <p className="text-xs text-text-muted">Code</p>
                    <p className="text-sm font-medium text-text font-mono">{viewing.designationCode}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-background rounded-lg p-3 border border-border">
                  <div className="p-2 bg-primary/10 rounded-lg"><Layers size={15} className="text-primary" /></div>
                  <div>
                    <p className="text-xs text-text-muted">Level</p>
                    <p className="text-sm font-medium text-text">{viewing.level ?? '—'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-background rounded-lg p-3 border border-border">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    {viewing.isActive ? <CheckCircle size={15} className="text-primary" /> : <XCircle size={15} className="text-primary" />}
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">Status</p>
                    <p className={`text-sm font-medium ${viewing.isActive ? 'text-success' : 'text-danger'}`}>
                      {viewing.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-background rounded-lg p-3 border border-border">
                <div className="p-2 bg-primary/10 rounded-lg"><AlignLeft size={15} className="text-primary" /></div>
                <div className="min-w-0">
                  <p className="text-xs text-text-muted">Description</p>
                  <p className="text-sm text-text whitespace-pre-wrap break-words">{viewing.description || '—'}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-4 py-4 border-t border-border">
              <button
                type="button"
                onClick={() => setViewing(null)}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => { const d = viewing; setViewing(null); startEdit(d); }}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Edit2 size={16} />
                Edit Designation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text">{editing ? 'Edit Designation' : 'New Designation'}</h2>
              <button onClick={resetForm} className="p-1 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Name *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Senior Doctor"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Designation Code *</label>
                <input
                  required
                  value={form.designationCode}
                  onChange={(e) => {
                    setCodeEdited(true);
                    setForm({ ...form, designationCode: e.target.value.toUpperCase() });
                  }}
                  placeholder="e.g. SENIOR_DOCTOR"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                />
                <p className="mt-1 text-xs text-text-muted">Auto-generated from the name. You can edit it.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Level</label>
                <input
                  type="number"
                  value={form.level}
                  onChange={(e) => setForm({ ...form, level: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional description"
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="desigIsActive" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                <label htmlFor="desigIsActive" className="text-sm text-text">Active</label>
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <button type="button" onClick={resetForm} className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors">Cancel</button>
                <button type="submit" disabled={createDesig.isPending || updateDesig.isPending} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                  {(createDesig.isPending || updateDesig.isPending) && <Loader2 size={16} className="animate-spin" />}
                  {editing ? 'Update Designation' : 'Create Designation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete designation?"
        description={
          <>
            This will delete <strong className="text-text">{deleteTarget?.name}</strong>. This
            cannot be undone.
          </>
        }
        confirmLabel="Delete designation"
        loading={deleteDesig.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} designation${selectedIds.size === 1 ? '' : 's'}?`}
        description="This action will remove the selected designations. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} designation${selectedIds.size === 1 ? '' : 's'}`}
        loading={bulkDeleteDesig.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
